import React, { createContext, useState, useEffect, ReactNode } from "react";
import { getInputsTextCreationAI } from "../api/";
import { useLanguage } from "./useLanguage";
import { GlobalState, GlobalStateContextProps } from "./types";

// Create the context with an undefined default value
export const GlobalStateContext = createContext<GlobalStateContextProps | undefined>(undefined);

// Define the props for the GlobalStateProvider
interface GlobalStateProviderProps {
    children: ReactNode;
}

// Global State Provider
export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({ children }) => {
    const [data, setData] = useState<GlobalState>({
        categoriesAI: [],
        languagesAI: [],
        themeAI: [],
        targetAudienceAI: [],
        locationCityOptions: [],
        locationCountryOptions: [],
        guides: [],
        categories: [],
        filtersAI: [],
    });

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem("token"));

    const { language } = useLanguage();

    // Fetch data function
    const fetchData = async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
            const response = await getInputsTextCreationAI({ token, language });
            if (response.error) {
                throw new Error("Error fetching data");
            }
            if (response.data) {
                setData(response.data);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error");
        } finally {
            setLoading(false);
        }
    };

    // Listen for changes in localStorage token
    useEffect(() => {
        const handleStorageChange = () => {
            const newToken = localStorage.getItem("token");
            if (newToken !== token) {
                setToken(newToken);
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [token]);

    // Fetch data when token or language changes
    useEffect(() => {
        if (token) {
            fetchData();
        }
    }, [token, language]);

    return (
        <GlobalStateContext.Provider value={{ data, loading, error, refetch: fetchData, setToken }}>
            {children}
        </GlobalStateContext.Provider>
    );
};
